import { BaseMapper } from 'src/data/common/BaseMapper'
import { LineItemMapper } from 'src/data/orders/LineItemMapper'
import { PdMapper } from 'src/data/pd/PdMapper'
import { RxMapper } from 'src/data/rx/RxMapper'
import { countryNamebyCode } from 'src/utils/helper'

import { OrderTaxeMapper } from './OrderTaxeMapper'
import {
  LineItemModel,
  OrderAPI,
  OrderModel,
  PaginateOrdersAPI,
  PaginateOrdersModel,
} from './type'
import { CustomerMapper } from '../customers/CustomerMapper'

const getExtraFees = (lineItems: LineItemModel[]): LineItemModel[] => {
  const other = {
    id: 0,
    title: 'Other',
    type: 'other',
    price: lineItems.reduce(
      (acc, curr) =>
        ['other', 'store_discretion'].includes(curr.type)
          ? acc + curr.price
          : acc,
      0
    ),
  } as LineItemModel

  const items = [
    ...lineItems.filter(
      (item) => !['other', 'store_discretion'].includes(item.type)
    ),
    other,
  ]

  return items.sort((a, b) => (a.type > b.type ? 1 : b.type > a.type ? -1 : 0))
}

export class OrderMapper extends BaseMapper {
  getDisplayStreet = (street_1: string, street_2: string): string => {
    if (street_2) {
      return `${street_1}, ${street_2}`
    }
    return street_1
  }

  getDisplayLocation = (city: string, zone: string, zip: string): string =>
    `${city}, (${zone}) ${zip}`

  apiMapper = (order: OrderAPI): OrderModel => ({
    id: order.id,
    order_date: order.original_order_date
      ? order.original_order_date
      : order.create_date,
    status: order.state,
    can_return: order.can_return,
    nb_can_return: order.nb_can_return,
    has_redo:
      order.nb_can_return <
      order.order_products.filter((op) => !op.original_order_product).length,
    balance: order.balance,
    currency: order.currency,
    price: order.price,
    is_home_try_on: order.is_home_try_on,
    has_predelivery_redo: order.has_predelivery_redo,
    is_rx_required: order.is_rx_required,
    action_required:
      order.state !== 'canceled' &&
      ((order.is_rx_required && (!order.pd || !order.rx)) ||
        order.balance > 0 ||
        order.price < 0),
    shipping: {
      city: order.delivery_city,
      company: order.delivery_company,
      country_code: order.delivery_country_code,
      country_name: countryNamebyCode(order.delivery_country_code) ?? '',
      date: order.delivery_date,
      name: `${order.delivery_first_name} ${order.delivery_last_name}`,
      phone: order.delivery_phone,
      postal_code: order.delivery_postal_code,
      street_1: order.delivery_street1,
      street_2: order.delivery_street2,
      zone: order.delivery_zone_code,
      display_street: this.getDisplayStreet(
        order.delivery_street1,
        order.delivery_street2
      ),
      display_location: this.getDisplayLocation(
        order.delivery_city,
        order.delivery_zone_code,
        order.delivery_postal_code
      ),
    },
    billing: {
      city: order.billing_city,
      company: order.billing_company,
      country_code: order.billing_country_code,
      country_name: countryNamebyCode(order.billing_country_code) ?? '',
      date: order.billing_date,
      name: `${order.billing_first_name} ${order.billing_last_name}`,
      phone: order.billing_phone,
      postal_code: order.billing_postal_code,
      street_1: order.billing_street1,
      street_2: order.billing_street2,
      zone: order.billing_zone_code,
      display_street: this.getDisplayStreet(
        order.billing_street1,
        order.billing_street2
      ),
      display_location: this.getDisplayLocation(
        order.billing_city,
        order.billing_zone_code,
        order.billing_postal_code
      ),
    },
    order_taxes: new OrderTaxeMapper().getMappedObject({
      data: order.order_taxes,
      from: 'api',
    }),
    nb_redos: order.nb_redos,
    rma_type:
      order.nb_can_return === 0
        ? 'complet'
        : order.nb_can_return <
          order.order_products.filter((op) => !op.original_order_product).length
          ? 'partial'
          : null,
    destination: order.destination,
    subtotal: order.order_products.reduce((prev, current) => {
      if (!current.reverse_order_product && current.quantity > 0) {
        return prev + current.price
      }
      return prev
    }, 0),
    line_items: getExtraFees(
      new LineItemMapper().getMappedObject({
        data: order.line_items,
        from: 'api',
      })
    ),
    origin: order.origin,
    created_by:
      order.created_by.first_name && order.created_by.last_name
        ? `${order.created_by.first_name} ${order.created_by.last_name.toUpperCase()[0]
        }.`
        : '',
    rx: order.rx
      ? new RxMapper().getMappedObject({ data: order.rx, from: 'api' })
      : RxMapper.defaultValue,
    pd: order.pd
      ? new PdMapper().getMappedObject({ data: order.pd, from: 'api' })
      : PdMapper.defaultValue,
    customer_id: order.customer_id,
    customer: new CustomerMapper().getMappedObject({ data: order.customer, from: 'api' }),
    customer_form: order.customer_form,
    order_email: order.order_email,
    drupal_order_id: order.drupal_order_id,
    displayed_id:
      order.drupal_order_id === order.id ? order.id : order.drupal_order_id,
  })

  public static defaultValue: OrderModel = {
    id: 0,
    order_date: '',
    status: '',
    balance: 0,
    displayed_id: 0,
    drupal_order_id: 0,
    subtotal: 0,
    price: 0,
    currency: 'CAD',
    is_home_try_on: false,
    is_rx_required: false,
    action_required: false,
    has_predelivery_redo: false,
    has_redo: false,
    shipping: {
      city: '',
      company: '',
      country_code: '',
      country_name: '',
      date: '',
      name: '',
      phone: '',
      postal_code: '',
      street_1: '',
      street_2: '',
      zone: '',
      display_street: '',
      display_location: '',
    },
    billing: {
      city: '',
      company: '',
      country_code: '',
      country_name: '',
      date: '',
      name: '',
      phone: '',
      postal_code: '',
      street_1: '',
      street_2: '',
      zone: '',
      display_street: '',
      display_location: '',
    },
    order_taxes: [],
    line_items: [],
    nb_redos: 0,
    nb_can_return: 0,
    rma_type: null,
    can_return: false,
    destination: '',
    origin: '',
    created_by: '',
    rx: RxMapper.defaultValue,
    pd: PdMapper.defaultValue,
    customer_id: 0,
    customer: CustomerMapper.defaultValue,
    customer_form: null,
    order_email: '',
  }
}

export class PaginatedOrderMapper extends BaseMapper {
  apiMapper = (pagination: PaginateOrdersAPI): PaginateOrdersModel => {
    return {
      count: pagination.count,
      page_count: pagination.page_count,
      results: new OrderMapper().getMappedObject({
        from: 'api',
        data: pagination.results,
      }),
    }
  }
}
